import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { boardsApi, foldersApi, type Board, type BoardFolderTree } from '@/api/boardsApi'
import { errorMessage } from '@/components/common/States'
import { useBoardMutation } from '@/hooks/useBoards'
import { placeBoard } from '@/lib/boardTree'
import { useToast } from '@/providers/ToastProvider'

/** Star, duplicate, archive / restore and file a board, with a toast for each outcome. */
export function useBoardActions(folders: BoardFolderTree | undefined) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { notify } = useToast()
  const fail = (e: unknown) => notify({ title: errorMessage(e), tone: 'error' })

  const star = useBoardMutation(({ id, starred }: { id: string; starred: boolean }) => boardsApi.star(id, starred))
  const duplicate = useBoardMutation((id: string) => boardsApi.duplicate(id))
  const archive = useBoardMutation(({ id, archived }: { id: string; archived: boolean }) => boardsApi.update({ id, archived }))
  const place = useBoardMutation(foldersApi.setBoards)

  return {
    pending: star.isPending || duplicate.isPending || archive.isPending || place.isPending,
    toggleStar: (b: Board) => star.mutate({ id: b.id, starred: !b.starred }, { onError: fail }),
    duplicate: (b: Board) =>
      duplicate.mutate(b.id, {
        onSuccess: (copy) => {
          notify({ title: t('boards.duplicated', { name: copy.name }) })
          navigate(`/boards/${copy.id}`)
        },
        onError: fail,
      }),
    setArchived: (b: Board, archived: boolean) =>
      archive.mutate(
        { id: b.id, archived },
        { onSuccess: () => notify({ title: t(archived ? 'boards.archived' : 'boards.restored', { name: b.name }) }), onError: fail },
      ),
    /** File the board in `folderId`, or take it out of its folder of that scope (null). */
    moveTo: (b: Board, scope: 'personal' | 'team', folderId: string | null) => {
      const list = scope === 'personal' ? (folders?.personal ?? []) : (folders?.team?.folders ?? [])
      const write = placeBoard(list, b.id, folderId)
      if (!write) return
      const folder = list.find((f) => f.id === write.folderId)
      place.mutate(write, {
        onSuccess: () =>
          notify({ title: t(folderId ? 'boards.movedTo' : 'boards.removedFrom', { name: b.name, folder: folder?.name ?? '' }) }),
        onError: fail,
      })
    },
  }
}
